import { Accordion, AccordionDetails, AccordionSummary, Button, CircularProgress, Grid, Typography } from "@material-ui/core";
import { ChevronRight, ExpandMore } from "@material-ui/icons";
import { useEffect, useRef, useState } from "react";

export default function ProductLinkList(props) {
  const [offset, setOffset] = useState(0);
  const [productList, setProductList] = useState([]);
  const [hasMoreItems, setHasMoreItems] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isExpanded, setIsExpanded] = useState(props.expanded || false);
  const [hasRequested, setHasRequested] = useState(false);
  const rootRef = useRef(null);

  const updateProductList = async (productUUID, newOffset, limit) => {
    setIsLoadingData(true);
    setOffset(newOffset);

    const results = await props.getNextProductGroup?.(productUUID, newOffset, limit);

    setHasMoreItems(results.data.has_more_items);
    setProductList([...productList, ...results.data.products]);
    setIsLoadingData(false);
  }

  // Reset when the product changes so the deferred fetch re-arms.
  useEffect(() => {
    setProductList([]);
    setOffset(0);
    setHasRequested(false);
  }, [props.productUUID])

  // Defer the initial fetch until this section scrolls near the viewport. These
  // groups are below the fold, so loading them on mount just steals DB connections
  // from the product/price query on first load.
  useEffect(() => {
    if (hasRequested || !props.productUUID || !props.getNextProductGroup) return;

    const el = rootRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // Fallback: no observer support — fetch immediately.
      setHasRequested(true);
      updateProductList(props.productUUID, 0, props.limit);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setHasRequested(true);
        updateProductList(props.productUUID, 0, props.limit);
        observer.disconnect();
      }
    }, { rootMargin: '300px' });

    observer.observe(el);
    return () => observer.disconnect();
  }, [props.productUUID, props.getNextProductGroup, hasRequested])

  if (!productList?.length) {
    // Keep an observable sentinel in the layout so the observer can fire.
    return <div ref={rootRef} />;
  }

  return (
    <Accordion ref={rootRef} style={{backgroundColor: "#00000017", margin: "0px 0px 15px"}} expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Typography variant="h6" style={{margin: "0px"}}><b>{props.sectionName}</b></Typography>
      </AccordionSummary>
      <AccordionDetails style={{backgroundColor: "#FFFFFF", border: "1px solid #00000017", padding: "20px 10px"}}>
        <Grid container spacing={2}>
          { 
            productList?.map(p => {
              return (
                <Grid item key={p.product_uuid + `${p.distributor_name ? '_' + p.distributor_name : ''}`} xs={12} sm={6} md={4} lg={3}>
                  <Button 
                    variant="contained" 
                    href={`/product/${p.product_uuid}`} 
                    fullWidth 
                    color="inherit"
                  >
                    <Grid container alignItems="center">
                      <Grid item xs={11}>
                        { props.showDistributor === false ? undefined : (
                            <Typography variant="caption" style={{display: "block"}}>{p.distributor_name}</Typography>
                          )
                        }
                        <Typography variant="body2" style={{display: "block", marginTop: "10px"}}><b>{p.trade_name} {p.size_cd && !(p.trade_name).includes(p.size_cd) ? `(${p.size_cd})` : ''}</b></Typography>
                        <Typography variant="caption" style={{display: "block"}}>{p.style}</Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <ChevronRight fontSize="large"/>
                      </Grid>
                    </Grid>
                  </Button>
                </Grid>
              )
            })
          }  
          { hasMoreItems ? (
              <Grid item xs={12}>
                <Button 
                  variant="contained" 
                  size="large"
                  style={{display: "block", color: "#28C4FC", backgroundColor: "#142E3E", width: "80%", maxWidth: "600px", margin: "20px auto 0px"}}
                  onClick={() => updateProductList(props.productUUID, offset + props.limit + 1, props.limit)}
                >
                  {isLoadingData ? (
                      <>
                        <CircularProgress size={20} style={{marginRight: "10px"}}/> Loading...
                      </>
                    ) : 'Load More...'
                  }
                </Button>
              </Grid>
            ) : undefined
          }
        </Grid>
      </AccordionDetails>
    </Accordion> 
  )
}